"use client";

import { DataTableColumnHeader } from "@/components/dataTables/dataTableColumnHeader";
import { Button } from "@/components/ui/Button";
import { CuteWordOrb } from "@/components/ui/CuteWordOrb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Link } from "@/components/ui/Link";
import { TooltipWrapper } from "@/components/ui/TooltipWrapper";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { FlattenedControl } from "@/packages/common/src/utils/securityControlsHelper";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

/*
Note: Columns are where you define the core of what your table will look like. 
They define the data that will be displayed, how it will be formatted, sorted and filtered.
*/

// Create column definitions for your security controls table
export const controlsColumnsDef = (): ColumnDef<FlattenedControl>[] => [
  {
    accessorKey: "title",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Title" />;
    },
    cell: ({ row }) => {
      return <p className="font-medium text-text-darkBlue">{row.original.title}</p>;
    },
  },
  {
    id: "description",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Description" />;
    },
    cell: ({ row }) => {
      // Get first standard's description or show a default message
      const standardKeys = Object.keys(row.original.standards);
      const description = standardKeys.length > 0 ? row.original.standards[standardKeys[0]].description : "No description available";

      return <p className="text-center">{description}</p>;
    },
  },
  {
    id: "category",
    filterFn: (row, id, value) => {
      if (!value) return true;
      // row is the row object,
      // id is the column id,
      // value is the filter value. It is the values of the filter specifiers
      const rowCategoryArr = Array.isArray(row.original.category) ? row.original.category : [row.original.category];
      const valueArr = Array.isArray(value) ? value : [value];
      //check if any of the values in the value array are in the row category array
      return valueArr.some((v) => rowCategoryArr.includes(v));
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Category" />;
    },
    cell: ({ row }) => {
      // Get first standard's category or show a default message
      const standardKeys = Object.keys(row.original.standards);
      const category = standardKeys.length > 0 ? row.original.standards[standardKeys[0]].category : "Uncategorized";

      // Handle both string and string[] cases
      if (Array.isArray(category)) {
        return (
          <div className="flex flex-wrap gap-1 justify-center">
            {category.map((cat, index) => (
              <span key={index} className="text-center">
                {cat}
              </span>
            ))}
          </div>
        );
      }

      return <p className="text-center">{category}</p>;
    },
  },
  {
    id: "standards",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Standards" />;
    },
    cell: ({ row }) => {
      const standardKeys = Object.keys(row.original.standards);
      return (
        <div className="flex flex-wrap gap-1 justify-center">
          {standardKeys.map((key) => (
            <CuteWordOrb key={key} text={key} />
          ))}
        </div>
      );
    },
  },
];
